import { isStringDecimalNumber } from 'src/common/helpers/validators/isStringDecimalNumber'
import { stripCharacters } from 'src/common/helpers/sanitisers/stripCharacters'
import { currenciesMap } from './currency'

/**
 * Find the currency in the currency list and returns the currency object
 * @param {string} currencyName
 */
export const getCurrencyByName = (currencyName) => {
  let currencyObj = currenciesMap.get(currencyName.toUpperCase())
  if (!currencyObj) {
    console.error(`'${currencyName}' is not a valid currency. 'GBP' will be used as the fallback currency.`)
    currencyObj = currenciesMap.get('GBP')
  }

  const currency = {
    decimal: currencyObj.currency_decimal_separator,
    thousands: currencyObj.currency_thousands_separator,
    precision: currencyObj.currency_decimal_number,
    prefix: currencyObj.currency_sign_position === 'before' ? `${currencyObj.currency_sign}` : '',
    suffix: currencyObj.currency_sign_position === 'after' ? ` ${currencyObj.currency_sign}` : '',
  }
  return currency
}

/**
 * Checks the currency json list for the given currency name and return true if it is available
 * @param {string} currencyName
 */
export const isCurrencyValid = (currencyName) => {
  return currenciesMap.has(currencyName.toUpperCase())
}

/**
 * Gets the currency characters that need to be removed in order to parse the value as a number
 * @param {string} currencyName
 * @returns {string[]}
 */
export const getCurrencyCharactersToRemove = (currencyName) => {
  const currency = getCurrencyByName(currencyName)
  const charactersToRemove = [currency.prefix, currency.suffix, currency.thousands]
  return charactersToRemove.filter((c) => {
    // In some cases, the currency may not have a specific character as prefix or suffix or thousands.
    // In such cases, the character is defined as '' that need to be ignore in the output list.
    return c !== ''
  })
}

/**
 * Counts the decimal points based on the currency's decimal character
 * @param {string} number
 * @param {string} currencyName
 * @returns {number}
 */
const countDecimalPoints = (number, currencyName) => {
  const currency = getCurrencyByName(currencyName)
  const numString = number.toString()
  const decimalPart = numString.split(currency.decimal)[1]

  if (decimalPart) return decimalPart.length
  else return 0
}

/**
 * Sanitises the value by checking the following conditions
 * 1. If the value ends with the decimal point character, the value is returned as it is to allow the user to type the full number
 * 2. If the value ends with 0 and the number of decimal points is less than the currency's precision, the value is returned as it is to allow the user to type the full number
 * 3. If the value ends with 0, but the number of decimal points is greater than the currency's precision, the value is converted to the number with the currency's precision
 * Finally, if the value is valid, the number is converted to string and the default decimal point is replaced with the currency's decimal point
 * @param {string} stringValue
 * @param {number} numberValue
 * @param {string} currencyName
 * @returns {string}
 */
export const sanitiseValue = (stringValue, numberValue, currencyName) => {
  const currency = getCurrencyByName(currencyName)
  return stringValue.endsWith(currency.decimal)
    ? stringValue
    : stringValue.endsWith('0') && countDecimalPoints(stringValue, currencyName) <= currency.precision
      ? stringValue
      : countDecimalPoints(stringValue, currencyName) > currency.precision
        ? stringValue.substring(
            0,
            stringValue.length - (countDecimalPoints(stringValue, currencyName) - currency.precision),
          )
        : numberValue.toString().replace('.', currency.decimal)
  // The reason why 'numberValue' is assigned to obj.value as fallback condition is to ensure that the input always has the number 0 inside
  // If we replace 'numberValue' with 'stringValue', the user would be able to remove the '0' character, making the input empty.
}

/**
 * Adds the currency's thousands character to the number for every 3 digits
 * @param {string} number
 * @param {string} currencyName
 * @returns {string}
 */
export const addThousandsPoints = (number, currencyName) => {
  const currency = getCurrencyByName(currencyName)
  const numberString = number.toString()
  const [integerPart, decimalPart = ''] = numberString.split(currency.decimal)
  const commaNumber = new Intl.NumberFormat().format(integerPart)
  const integerPartFormatted = commaNumber.replace(/,/g, currency.thousands)

  if (numberString.endsWith(currency.decimal)) {
    return `${integerPartFormatted}${currency.decimal}`
  } else if (decimalPart === '') return integerPartFormatted
  else return `${integerPartFormatted}${currency.decimal}${decimalPart}`
}

/**
 * Converts the string number to a valid number with standard characters,
 * converts the number to a decimal number with the specified number of decimal places,
 * and replaces the standard characters with the currency characters
 * @param {string} number
 * @param {string} currencyName
 * @returns
 */
export const convertNumberToFixed = (number, currencyName, precision = null) => {
  const currency = getCurrencyByName(currencyName)
  const charactersToRemove = getCurrencyCharactersToRemove(currencyName)
  let stringValue = stripCharacters(number.toString().trim(), charactersToRemove)
  if (stringValue.endsWith(currency.decimal)) {
    stringValue += '0'
  }
  let convertedNumber
  if (precision !== null) {
    convertedNumber = Number(stringValue.replace(currency.decimal, '.')).toFixed(precision)
  } else {
    convertedNumber = Number(stringValue.replace(currency.decimal, '.')).toFixed(currency.precision)
  }
  return convertedNumber.replace('.', currency.decimal)
}

/**
 * Formats the input number based on the provided currency
 * @param {number|string} number
 * @param {string} currencyName
 */
export const formatMonetaryAmount = (number, currencyName, precision = null) => {
  const currency = getCurrencyByName(currencyName)
  const charactersToRemove = getCurrencyCharactersToRemove(currencyName)
  let stringValue = stripCharacters(number.toString().trim(), charactersToRemove)
  if (stringValue.includes('.')) {
    stringValue = stringValue.replace('.', currency.decimal)
  }
  if (!isStringDecimalNumber(stringValue, currency.decimal)) stringValue = '0'
  const numberValue = Number(stringValue.replace(currency.decimal, '.'))

  const sanitisedValue = sanitiseValue(stringValue, numberValue, currencyName)
  const fixedValue = convertNumberToFixed(sanitisedValue, currencyName, precision)
  const thousandsSeparatedValue = addThousandsPoints(fixedValue, currencyName)

  return `${currency.prefix}${thousandsSeparatedValue}${currency.suffix}`
}

/**
 * Converts a formatted currency string back to its numeric value
 * @param {string} formattedValue - e.g. "£ 0.23" or "€ 1.234,56"
 * @param {string} currencyName - e.g. "GBP"
 * @returns {number} - the numeric value, e.g. 0.23
 */
export const parseMonetaryAmount = (formattedValue, currencyName) => {
  const currency = getCurrencyByName(currencyName)
  const charactersToRemove = getCurrencyCharactersToRemove(currencyName)

  // Remove currency symbols, thousand separators, etc.
  const cleaned = stripCharacters(formattedValue.toString().trim(), charactersToRemove).replace(currency.decimal, '.')

  const numericValue = parseFloat(cleaned)

  if (isNaN(numericValue)) return 0

  return numericValue
}
